'use client';
import {getStrapiData} from '@/app/lib/api';
import {useMenu} from '@/context/MenuContext';
import clsx from 'clsx';
import {useLocale, useTranslations} from 'next-intl';
import {useEffect, useRef, useState} from 'react';
import {useDomHandler} from './helpers/dom-handler';
import DateBlock from './tools/DateBlock';
import LocaleSwitcher from './tools/LocaleSwitcher';

export default function Navigation({menu, siteLogo}: any) {
  useDomHandler();
  const t = useTranslations('header');
  const u = useTranslations('Utils');
  const locale = useLocale();
  const {items, options} = menu ?? {items: [], options: {}};
  const [showHeader, setShowHeader] = useState(false);

  const {
    isShowMenu,
    showMenu,
    hideMenu,
    activeMenu,
    isShowMobileMenu,
    setIsShowMobileMenu
  } = useMenu();

  const menuList: {
    key: string;
    url: string;
    title: string;
  }[] = items ?? [];

  const theySayList = options?.theySay?.items ?? [];
  const activityItems = options?.activity?.items ?? [];
  const keywordRef = useRef<HTMLInputElement>(null);

  const awardsList = [
    {
      image: options?.album?.primaryCover,
      title: t('subMenu.primarySchoolWinner'),
      url: options?.album?.primaryUrl
    },
    {
      image: options?.album?.secondaryCover,
      title: t('subMenu.secondaryAndSpecialSchoolWinner'),
      url: options?.album?.secondaryUrl
    }
  ];

  const [albumList, setAlbumList] = useState<
    {slug: string; short_title: string; title: string}[]
  >([]);

  const [latestPost, setLatestPost] = useState<{
    slug: string;
    thumbnail: {
      url: string;
    };
    title: string;
    posted_at: Date;
  }>();

  const [allPostUrl, setAllPostUrl] = useState('');

  const searchKeyword = () => {
    if (keyword.trim() != '') {
      window.location.href = '/search/?q=' + encodeURIComponent(keyword.trim());
    }
  };

  useEffect(() => {
    getStrapiData(locale, 'galleries', {sort: 'slug:desc'}).then((r) =>
      setAlbumList(r.data)
    );

    getStrapiData(locale, 'posts', {
      populate: ['thumbnail'],
      sort: 'posted_at:desc'
    }).then((r) => {
      if (r.data) {
        setLatestPost(r.data?.[0]);
        setAllPostUrl('/article/' + r.data?.[0]?.slug);
      }
    });

    document.addEventListener('keydown', function (event) {
      const activeElement = document.activeElement;
      if (activeElement && activeElement.tagName === 'A' && event.key === ' ') {
        event.preventDefault();
      }
    });

    document.querySelector('main')?.addEventListener('mouseenter', () => {
      hideMenu();
    });

    const urlParams = new URLSearchParams(window.location.search);
    if (!window.location.pathname.includes('/nomination/')) {
      if (urlParams.get('popup') !== '1') {
        setShowHeader(true);
      }
    }
  }, []);

  useEffect(() => {
    if (activeMenu == 'menu-search') {
      keywordRef.current?.focus();
    }
  }, [isShowMenu]);

  const toggleMobileMenu = () => {
    setIsShowMobileMenu(!isShowMobileMenu);
    //     $("#mobile").toggleClass("overflow-hidden h-screen");
  };
  const toggleMenu = (key: string) => {
    if (activeMenu == key) {
      hideMenu();
    } else {
      showMenu(key);
    }
  };

  const [keyword, setKeyword] = useState('');

  return (
    showHeader && (
      <header className={'relative z-50 bg-white common-layout'}>
        <a
          href="#main"
          className={clsx(
            'absolute left-0 -top-[40px] focus:top-0',
            'text-black p-2 bg-white z-200',
            'transition-[top] duration-[0.3s] ease-in-out'
          )}
        >
          {t('skipToMain')}
        </a>
        <nav
          className="bg-clifford fixed top-0 left-0 w-full bg-white/10 z-[20]"
          style={{boxShadow: '0 4px 10px rgba(50, 50, 50, 0.3)'}}
        >
          <div className="mx-auto">
            <div
              id="menu-nav"
              className="flex justify-between items-center h-[15vw] md:h-[6.2vw] text-[--color-menu] bg-[--bg-menu] transition duration-500"
            >
              <div
                className="flex flex-shrink-0 hidden md:flex bg-white h-full justify-center items-center w-[23vw]"
                style={{
                  clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0 100%)',
                  transform: 'translateZ(0)',
                  willChange: 'clip-path',
                  isolation: 'isolate'
                }}
              >
                <a href="/" id="desktop-site-logo">
                  <img
                    className="h-[10vw] md:h-[4.3vw] w-auto ml-[3vw] mr-[4vw] pointer-events-none"
                    src={siteLogo}
                    alt={t('menu.home')}
                  />
                </a>
              </div>

              <div
                className="flex-shrink-0 md:hidden bg-white h-full flex justify-start items-center  w-[55vw]"
                style={{
                  clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0 100%)',
                  transform: 'translateZ(0)',
                  willChange: 'clip-path',
                  isolation: 'isolate'
                }}
              >
                <a href="/" id="mobile-site-logo">
                  <img
                    className="h-[10vw] md:h-[4.5vw] w-auto ml-[5vw]"
                    src={siteLogo}
                    alt={t('menu.home')}
                  />
                </a>
              </div>

              <div className="menu hidden md:flex items-center gap-10  ">
                {menuList.map((m) => {
                  return (
                    <div
                      key={'menu-' + m.key}
                      className={`header-title text-[--color-menu] hover:text-[--color-menu] hover-underline`}
                      onMouseEnter={() => {
                        showMenu(m.key);
                        setTimeout(() => {
                          (document.activeElement as any)?.blur();
                        }, 100);
                      }}
                    >
                      <div>
                        <div className="flex">
                          {m.url == '#' ? (
                            <>{m.title}</>
                          ) : (
                            <a href={m.url} role="button">
                              {m.title}
                            </a>
                          )}
                          <button
                            id={m.key}
                            className="flex items-center"
                            onClick={() => {
                              activeMenu == m.key
                                ? hideMenu()
                                : showMenu(m.key);
                            }}
                            aria-label={m.title}
                          >
                            <img
                              className="hidden icon-dark w-[0.6vw] object-contain ml-[0.5vw]"
                              src="/assets/images/header/icon-arrow-dark.png"
                            />
                            <img
                              className="icon-white w-[0.6vw] object-contain ml-[0.5vw]"
                              src="/assets/images/header/icon-arrow-white.png"
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mr-[10vw] hidden md:flex  items-center w-[7vw] text-[--color-menu] hover:text-[--color-menu]">
                <LocaleSwitcher />
                <button
                  id="menu-search"
                  className="h-[1vw] w-[1vw] ml-[1vw] on on-hover-focusa-[#keyword]"
                  onMouseEnter={() => {
                    showMenu('menu-search');
                    (document.querySelector('#keyword') as any)?.focus();
                  }}
                  onClick={() => {
                    showMenu('menu-search');
                    (document.querySelector('#keyword') as any)?.focus();
                  }}
                  aria-label="search"
                >
                  <img
                    src="/assets/images/header/icon-search-white.png"
                    className="icon-white "
                  />
                  <img
                    src="/assets/images/header/icon-search-dark.png"
                    className="icon-dark hidden"
                  />
                </button>
              </div>

              <div className="md:hidden p-3">
                <button
                  id="mobile-menu-button"
                  className="text-black hover:text-gray-300 "
                  aria-label="mobile-menu"
                  onClick={() => {
                    toggleMobileMenu();
                  }}
                >
                  <img
                    src="/assets/images/header/bar-white.png"
                    className="w-[8vw] mr-[2vw] icon-white hidden"
                  />
                  <img
                    src="/assets/images/header/bar-dark.png"
                    className="w-[8vw] mr-[2vw] icon-dark"
                  />
                </button>
              </div>
            </div>
          </div>

          {isShowMobileMenu && (
            <div
              id="mobile-menu"
              className="menu md:hidden bg-white text-black"
            >
              <div
                id="mobile-menu-content"
                className="h-[calc(100vh-15vw)] flex flex-col gap-[3vw] px-[5vw] py-[8vw] font-[600] overflow-y-auto scroll-none"
              >
                <div className="pb-[20vh]">
                  <div className="flex pb-[2vw] border-b-[1px] border-[#DDDDDD] mb-[2vw] items-center">
                    <img
                      src="/assets/images/header/icon-search-dark.png"
                      className="h-[2.5vw] mr-[1vw] "
                    />
                    <input
                      type="text"
                      id="mobile-keyword"
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchKeyword()}
                      className="w-full outline-none"
                      style={{
                        outline: 'none',
                        border: 'none',
                        boxShadow: 'none',
                        marginBottom: '0px'
                      }}
                    />
                    <button
                      className="bg-black/55 text-white rounded-full px-[2vw] py-[0.5vw] flex items-center justify-center cursor-pointer font-[300] text-[0.875rem] w-[100px]"
                      onClick={() => searchKeyword()}
                    >
                      {u('search')}
                    </button>

                    <div className="relative w-[20vw] text-[--color-menu] hover:text-[--color-menu]">
                      <LocaleSwitcher />
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <div className="menu-group">
                      <button
                        className="flex justify-between items-center cursor-pointer py-[1vw] w-full"
                        onClick={() => {
                          toggleMenu('menu-about');
                        }}
                      >
                        <div className="title-h1">{t('menu.about')}</div>
                        <div>
                          <img
                            src="/assets/images/header/icon-arrow-dark.png"
                            className="h-[1.5vw]"
                          />
                        </div>
                      </button>
                      {isShowMenu && activeMenu === 'menu-about' && (
                        <div className="menu-section menu-about ">
                          <div>
                            <div className="font-[300] flex flex-col gap-[3vw]">
                              {options?.aboutSSA?.items?.map(
                                (item: any, idx: number) => (
                                  <a
                                    className="title-h3 menu-link"
                                    href={item.url}
                                    key={'menu_m_aboutSSA_' + idx}
                                  >
                                    <div>{item.text}</div>
                                  </a>
                                )
                              )}

                              <div className="flex gap-[2vw] mb-[0.5rem]">
                                {theySayList.map((t: any) => (
                                  <div className="relative" key={t.key}>
                                    <a href={t.url}>
                                      <div className="absolute bottom-[1.8vw] w-full px-[6vw] text-center flex flex-col items-center justify-center text-white">
                                        <div className="font-[500] leading-[3vw] text-[2vw] line-clamp-2">
                                          {t.theySay}
                                        </div>
                                        <div className="text-[2vw]">
                                          {t.name}
                                        </div>
                                      </div>
                                      <img className="rounded-md" src={t.img} />
                                    </a>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="menu-group">
                      <button
                        className="flex justify-between items-center cursor-pointer w-full"
                        onClick={() => {
                          toggleMenu('menu-activity');
                        }}
                      >
                        <div className="title-h1">
                          {options?.activity?.title}
                        </div>
                        <div>
                          <img
                            src="/assets/images/header/icon-arrow-dark.png"
                            className="h-[1.5vw]"
                          />
                        </div>
                      </button>
                      {isShowMenu && activeMenu === 'menu-activity' && (
                        <div className="menu-section menu-activity ">
                          <div>
                            <a href="/activity/">
                              <div className="relative mb-[2rem]">
                                <div className="absolute bottom-[1vw] left-[1vw] text-white text-[3vw] font-[500]">
                                  {options?.activity?.subtitle}
                                </div>
                                <img
                                  className="rounded-xl"
                                  src={options?.activity?.cover}
                                  alt={options?.activity?.subtitle}
                                />
                              </div>
                            </a>
                            <div className="font-[300] text-[3.2vw] flex flex-col gap-[3vw] px-[3vw]">
                              {options?.activity?.items?.map(
                                (item: any, idx: number) => (
                                  <a
                                    className="title-h3 menu-link"
                                    href={item.url}
                                    key={'menu_m_activity_' + idx}
                                  >
                                    {item.text}
                                  </a>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="menu-group">
                      <button
                        className="flex justify-between items-center cursor-pointer w-full"
                        onClick={() => {
                          toggleMenu('menu-announcements');
                        }}
                      >
                        <div className="title-h1">
                          {t('menu.announcements')}
                        </div>
                        <div>
                          <img
                            src="/assets/images/header/icon-arrow-dark.png"
                            className="h-[1.5vw]"
                          />
                        </div>
                      </button>
                      {isShowMenu && activeMenu === 'menu-announcements' && (
                        <div className="menu-section menu-announcements">
                          <div>
                            <div className="menu-subgroup">
                              <div className="title-h2">
                                {options?.winnerList?.title}
                              </div>
                              <div className="font-[300] text-[3.2vw] flex flex-col gap-[3vw] px-[3vw]">
                                {options?.winnerList?.items?.map(
                                  (item: any, idx: number) => (
                                    <a
                                      className="title-h3 menu-link"
                                      href={item.url}
                                      key={'m_menu_winnerList_' + idx}
                                    >
                                      {item.text}
                                    </a>
                                  )
                                )}
                              </div>
                            </div>

                            <div className="menu-subgroup">
                              <div className="title-h2">
                                {t('subMenu.publication')}
                              </div>
                              <div className="font-[300] text-[3.2vw] flex flex-col gap-[3vw] px-[3vw]">
                                {options?.publication?.items?.map(
                                  (item: any, idx: number) => (
                                    <a
                                      className="title-h3 menu-link"
                                      href={item.url}
                                      key={'m_menu_publication_' + idx}
                                    >
                                      {item.text}
                                    </a>
                                  )
                                )}
                              </div>
                            </div>

                            <div>
                              <div className="title-h2">
                                {t('subMenu.pressRelease')}
                              </div>
                            </div>

                            <div>
                              {latestPost && (
                                <a href={'/article/' + latestPost?.slug}>
                                  <div className="flex justify-between items-center bg-[#F7F8FA] rounded-[1vw] p-[2vw] gap-[1vw] mb-[5vw]">
                                    <div className="w-1/2">
                                      <div className="line-clamp-3 text-[0.875rem] font-[800] text-black">
                                        {latestPost?.title}
                                      </div>
                                      <div className="text-[0.625rem] font-[300] text-[#737373]">
                                        <DateBlock
                                          date={latestPost?.posted_at}
                                        />
                                      </div>
                                    </div>
                                    <div className="w-1/2">
                                      <img
                                        className="rounded-md"
                                        alt={latestPost?.title}
                                        src={latestPost?.thumbnail?.url}
                                      />
                                    </div>
                                  </div>
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="menu-group">
                      <button
                        className="flex justify-between items-center cursor-pointer w-full"
                        onClick={() => {
                          toggleMenu('menu-photos');
                        }}
                      >
                        <div className="title-h1">{t('menu.photos')}</div>
                        <div>
                          <img
                            src="/assets/images/header/icon-arrow-dark.png"
                            className="h-[1.5vw]"
                          />
                        </div>
                      </button>
                      {isShowMenu && activeMenu === 'menu-photos' && (
                        <div className="menu-section menu-photos">
                          <div>
                            <div className="menu-subgroup">
                              <div className="title-h2">
                                {options.album.year}
                              </div>
                              <div>
                                <div className="flex justify-between items-center gap-[2vw] text-[2.5vw] text-white ">
                                  {awardsList.map((a, i) => (
                                    <div
                                      className="w-1/2 relative"
                                      key={'award-m-' + i}
                                    >
                                      <a href={a.url}>
                                        <div className="absolute bottom-[1vw] left-[1vw]">
                                          {a.title}
                                        </div>
                                        <img
                                          className="rounded-md aspect-[2/1] object-cover object-top"
                                          src={a.image}
                                          alt={a.title}
                                        />
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>

                            <div className="menu-subgroup">
                              <div className="title-h2">
                                {t('subMenu.annualPhotos')}
                              </div>
                              <div className="grid grid-cols-4 font-[300] text-[0.875rem] flex flex-col gap-[3vw] ">
                                {albumList.map((album: any) => (
                                  <a
                                    className="text-black"
                                    href={'/gallery/' + album.slug}
                                    key={`album-m-${album.slug}`}
                                  >
                                    <div className="bg-[#E6E7E8] rounded-[1vw] flex items-center justify-center py-[1.5vw]">
                                      {album.short_title}
                                    </div>
                                  </a>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </nav>

        <div
          className={clsx(
            'dropdown-menu fixed top-[6.2vw] left-0 w-full z-[10] hidden md:block ',
            isShowMenu ? 'visible' : ''
          )}
          style={{boxShadow: '0 4px 10px rgba(50, 50, 50, 0.3)'}}
        >
          <div className="detail bg-white px-[10vw] relative text-black">
            <>
              <a
                className="close-menu"
                href="#"
                onFocus={() => {
                  hideMenu();
                  document.getElementById(activeMenu)?.focus();
                }}
              ></a>
            </>
            {isShowMenu && activeMenu === 'menu-about' && (
              <div className="menu-section menu-about flex justify-between mx-auto pt-[3vw] pb-[4vw]">
                <div className="flex flex-col w-1/3">
                  <div className="flex flex-col items-start justify-start gap-4 mr-[4vw]">
                    <div className="detail-title w-full">
                      <div>{options?.aboutSSA?.title ?? t('menu.about')}</div>
                    </div>

                    <div className="flex flex-col gap-y-[1.5vw]">
                      {options?.aboutSSA?.items?.map((i: any) => (
                        <a
                          className="detail-subtitle"
                          href={i.url}
                          key={i.text}
                        >
                          {i.text}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col w-2/3">
                  <div className="flex flex-col items-start gap-4 mr-[4vw]">
                    <div className="flex justify-between item-center w-full">
                      <div className="detail-title">
                        {options?.theySay?.title ?? t('subMenu.theySay')}
                      </div>
                      <a
                        href="/about/they-say/"
                        className="flex gap-x-[0.5vw] items-center detail-subtitle"
                      >
                        {u('viewAll')}
                        <img
                          className="h-[0.6vw]"
                          src="/assets/images/header/icon-arrow-r-dark.png"
                        />
                      </a>
                    </div>

                    <div className="flex items-center justify-between gap-[2vw] relative  hover-group">
                      {theySayList.map((t: any) => (
                        <div className="w-1/2 relative hover-item" key={t.key}>
                          <a href={t.url}>
                            <div className="absolute bottom-[1vw] w-full text-center text-white">
                              <div className="font-[500] my-[1vw] leading-[1vw] text-[1vw]">
                                <div>{t.theySay}</div>
                              </div>
                              <div className="text-[0.7vw]">{t.name}</div>
                            </div>
                            <img
                              className="rounded-2xl"
                              src={t.img}
                              alt={t.theySay + ' ' + t.name}
                            />
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {isShowMenu && activeMenu === 'menu-activity' && (
              <div className="menu-section menu-activity flex justify-between mx-auto pt-[3vw] pb-[4vw]">
                <div className="flex flex-col w-1/3">
                  <div className="flex flex-col items-start gap-4 mr-[4vw] text-black">
                    <div className="flex justify-between item-center w-full">
                      <div className="detail-title">
                        {options?.activity?.title ?? t('menu.activity')}
                      </div>
                    </div>

                    <div>
                      <div className="relative">
                        <a href="/activity/#event-schedule">
                          <div className="absolute bottom-[1vw] left-[1vw] text-white text-[0.7vw]">
                            {options?.activity?.subtitle}
                          </div>
                          <img
                            className="rounded-xl w-full"
                            src={options?.activity?.cover}
                            alt={options?.activity?.subtitle}
                          />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                {[
                  activityItems.slice(0, activityItems.length / 2),
                  activityItems.slice(activityItems.length / 2)
                ].map((items: any, idx: number) => (
                  <div className="flex flex-col w-1/3" key={'activity_' + idx}>
                    <div className="flex flex-col items-start gap-4 mr-[4vw]">
                      <div className="font-[800] mb-[1vw] text-[1vw] leading-1">
                        <div>&nbsp;</div>
                      </div>

                      <div className="flex flex-col gap-y-[1.5vw] font-[300] text-[0.8vw]">
                        {items?.map((item: any, idy: number) => (
                          <a
                            className="detail-subtitle"
                            href={item.url}
                            key={'activity_' + idx + idy}
                          >
                            {item.text}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {isShowMenu && activeMenu === 'menu-announcements' && (
              <div className="menu-section menu-announcements flex justify-between mx-auto pt-[3vw] pb-[4vw] gap-x-[5vw]">
                <div className="flex flex-col w-1/3">
                  <div className="flex flex-col items-start gap-4">
                    <div className="detail-title">
                      <div> {options?.winnerList?.title}</div>
                    </div>

                    <div className="flex flex-col gap-y-[1.5vw]">
                      {options?.winnerList?.items?.map(
                        (item: any, idx: number) => (
                          <a
                            className="detail-subtitle"
                            href={item.url}
                            key={'winnerList_' + idx}
                          >
                            {item.text}
                          </a>
                        )
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col w-1/4 mr-[1vw]">
                  <div className="flex flex-col items-start gap-4">
                    <div className="flex w-full justify-between item-center">
                      <div className="detail-title">
                        {t('subMenu.publication')}
                      </div>
                      <a
                        href="/publication/"
                        className="flex gap-x-[0.5vw] items-center detail-subtitle"
                      >
                        {u('viewAll')}
                        <img
                          className="h-[0.6vw]"
                          src="/assets/images/header/icon-arrow-r-dark.png"
                        />
                      </a>
                    </div>

                    <div className="flex flex-col gap-y-[1.5vw] font-[300]">
                      {options?.publication?.items?.map(
                        (item: any, idx: number) => (
                          <a
                            className="detail-subtitle"
                            href={item.url}
                            key={'publication_' + idx}
                          >
                            {item.text}
                          </a>
                        )
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col w-5/12">
                  <div className="flex flex-col items-start gap-4">
                    <div className="flex justify-between w-full">
                      <div className="detail-title">
                        {t('subMenu.pressRelease')}
                      </div>
                      <a
                        href={allPostUrl}
                        className="flex gap-x-[0.5vw] items-center detail-subtitle"
                      >
                        {u('viewAll')}
                        <img
                          className="h-[0.6vw]"
                          src="/assets/images/header/icon-arrow-r-dark.png"
                        />
                      </a>
                    </div>

                    {latestPost && (
                      <a
                        href={'/article/' + latestPost?.slug}
                        className="flex gap-x-[0.5vw] text-[0.8vw] font-[300] items-center"
                      >
                        <div className="flex gap-[2vw] bg-[#F7F8FA] rounded-[10px] p-[1vw] font-[300] text-black">
                          <div className="w-1/2">
                            <div className="font-[700] text-[0.875rem]">
                              <div className="line-clamp-3 w-full mb-3">
                                {latestPost?.title}
                              </div>
                            </div>
                            <div className="text-[#737373] detail-subtitle">
                              <DateBlock date={latestPost?.posted_at} />
                            </div>
                          </div>
                          <div className="w-1/2">
                            <img
                              src={latestPost?.thumbnail?.url}
                              alt={latestPost?.title}
                              className="rounded-xl"
                            />
                          </div>
                        </div>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
            {isShowMenu && activeMenu === 'menu-photos' && (
              <div className="menu-section menu-photos flex justify-between mx-auto py-[3vw]">
                <div className="flex flex-col w-[55%]">
                  <div className="flex flex-col items-start gap-4">
                    <div className="detail-title">
                      <div>{t('title.aswatson')}</div>
                      <div>{t('title.hkssa')}</div>
                    </div>

                    <div className="detail-title">
                      {options?.album?.year} <a href="#"></a>
                    </div>

                    <div className="w-full grid grid-cols-2 gap-4 pr-[4vw] content-awards hover-group">
                      {awardsList.map((a, i) => (
                        <div
                          className="relative w-full hover-item"
                          key={'award-' + i}
                        >
                          <a href={a.url}>
                            <img
                              className="rounded-2xl w-full h-[150px] object-cover object-top"
                              src={a.image}
                              alt={a.title}
                            />
                            <div className="absolute bottom-5 left-5 text-white font-[500] text-[0.8vw] w-[10vw]">
                              {a.title}
                            </div>
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="inline-block my-auto h-[300px] w-[2px] self-stretch bg-[#ddd]/50"></div>
                <div className="flex flex-col justify-start items-start w-[45%] pl-[5vw]">
                  <div className="flex flex-col w-full gap-4 mx-auto">
                    <div className="detail-title mb-[1.4vw]">
                      {t('subMenu.annualPhotos')}
                    </div>
                    <div className="grid grid-flow-row grid-cols-4 gap-y-[2vw] w-full text-[0.875rem] mb-[2vw] ">
                      {albumList.map((album: any) => (
                        <div
                          className="flex flex-col items-start gap-4 font-[300] w-[64px] xl:w-full"
                          key={`album-${album.slug}`}
                        >
                          <a
                            href={'/gallery/' + album.slug}
                            className="hover-underline text-black"
                          >
                            <div className="">{album.title}</div>
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {isShowMenu && activeMenu === 'menu-search' && (
              <div className="menu-section menu-search flex justify-between mx-auto pt-[6vw] pb-[7vw]">
                <div className="flex flex-col w-4/5 m-auto">
                  <div className="flex justify-between items-center gap-[2vw] border-b-[1px] border-gray-300 pb-[0.5vw]">
                    <div>
                      <img
                        src="/assets/images/header/icon-search-dark.png"
                        className="h=[1vw] w-[1vw]"
                      />
                    </div>
                    <div className="w-full">
                      <input
                        id="keyword"
                        name="keyword"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && searchKeyword()}
                        className="w-full text-[1vw] outline-none"
                        type="text"
                        ref={keywordRef}
                      />
                    </div>
                    <button
                      className="bg-black/55 text-white rounded-full flex py-[0.1rem] px-[0.5rem] items-center justify-center header-title cursor-pointer  w-[100px] hover:bg-black/75"
                      onClick={() => searchKeyword()}
                    >
                      {u('search')}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <>
              <a
                href="#"
                className="close-menu"
                onFocus={() => {
                  hideMenu();
                  console.log(activeMenu);
                  document.getElementById(activeMenu)?.focus();
                }}
              ></a>
            </>
          </div>
        </div>
      </header>
    )
  );
}
